import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl } from 'utils';
import { Card } from 'components/UI';
import { forceTimezone } from 'utils';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import useStyles from './DocumentsWidget.styled';
import { DocumentsActions } from 'state/modules/documents';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import VersionStatus from 'components/Documents/VersionStatus';
import DocumentModal from 'components/Documents/DocumentModal';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Box, Button, Typography, Icon } from '@material-ui/core';
import { transformAsyncErrors } from 'utils/utils';

const DocumentsWidget = ({ id, type }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { localeFormat } = useSelector((state) => state.organizations);
  const [openCreateDocumentModal, setCreateDocumentModal] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
    sort_desc: false,
    sort_field: 'id',
  });

  const [asyncErrors, setAsyncErrors] = useState({});

  const documents = useSelector((state) =>
    state.documents.all.map((id) => state.documents.byId[id])
  );

  const { pagination, loading } = useSelector((state) => state.documents);

  /**
   * Get documents
   * @param {object} params
   */
  const getDocuments = (params) => {
    const payload = {
      ...params,
    };
    if (id) {
      payload.source_id = id;
    }
    if (type) {
      payload.source_type = type;
    }
    dispatch(DocumentsActions.fetchDocuments(payload));
  };

  /**
   * Handle open/close create document modal
   */
  const handleOpenCreateDocumentModal = () => setCreateDocumentModal((prevValue) => !prevValue);

  /**
   * Handle create document
   */
  const handleCreateDocument = async (document_type_id, description) => {
    const payload = {
      document_type_id,
      description,
      source_id: id,
      source_type: type,
    };
    Object.keys(payload).forEach((key) => !payload[key] && delete payload[key]);
    const response = await dispatch(DocumentsActions.createDocument(payload));
    if (response.ok) {
      handleOpenCreateDocumentModal();
      const newParams = { ...params, page: 1 };
      setParams(newParams);
      getDocuments(newParams);
    } else if (response.problem === 'CLIENT_ERROR') {
      setAsyncErrors({
        ...asyncErrors,
        createDocument: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    getDocuments(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    getDocuments(newParams);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('documents.create_document'),
      can: { action: 'create', name: 'document' },
      onClick: handleOpenCreateDocumentModal,
    },
  ];

  /**
   * Render title of the widget
   */
  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="document-title" component="span">
          {i18n.t('documents.title')}
        </Box>
      </Typography>
      <Button
        type="button"
        variant="text"
        color="primary"
        disableFocusRipple={true}
        className={classes.button}
        data-cy="view-all-documents"
        onClick={() => {
          history.push(buildUrl(`/documents?source_id=${id}&source_type=${type}`));
        }}
      >
        {i18n.t('common.view_all')}
      </Button>
    </Box>
  );

  useEffect(() => {
    getDocuments(params);
  }, []); // eslint-disable-line

  return (
    <Card title={title} menuItems={items} data-cy="documents-card" headerActionType="vertical">
      <Box className={classes.container} data-cy="documents-list">
        {loading ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            {documents?.length > 0 ? (
              <>
                {documents.map((document, i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    data-cy={document?.name}
                    justifyContent="space-between"
                    className={classes.documentItem}
                    onClick={() => history.push(buildUrl(`/documents/${document?.id}`))}
                  >
                    <Box display="flex" width="60%">
                      <Box
                        className={clsx(classes.icon, classes[`icon${document?.default?.status}`])}
                      >
                        <Icon component={AttachFileIcon} />
                      </Box>
                      <Box ml={1} maxWidth="100%">
                        <Typography component="span">
                          <Box fontSize={13} fontWeight="fontWeightBold" className={classes.name}>
                            {document?.name || document?.document_type?.name}
                          </Box>
                        </Typography>
                        {document?.default?.expiration_time && (
                          <Typography color="textSecondary" component="span">
                            <Box fontSize={12}>
                              Expiration date:{' '}
                              {forceTimezone(moment(document?.default?.expiration_time)).format(
                                `${localeFormat} h:mm A`
                              )}
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                      {document?.default?.status && (
                        <VersionStatus status={document?.default?.status} />
                      )}
                      <Icon component={ChevronRightIcon} className={classes.arrowIcon} />
                    </Box>
                  </Box>
                ))}
                <Paginator
                  component="div"
                  limit={params.limit}
                  pagination={pagination}
                  rowsPerPageOptions={[3]}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            ) : (
              <Typography color="textPrimary">
                <Box fontSize={14} component="span">
                  {i18n.t(`documents.documents_placeholder`)}
                </Box>
              </Typography>
            )}
          </>
        )}
      </Box>
      {openCreateDocumentModal && (
        <DocumentModal
          mode="create"
          open={openCreateDocumentModal}
          handleCreate={handleCreateDocument}
          handleClose={handleOpenCreateDocumentModal}
          asyncErrors={asyncErrors.createDocument}
        />
      )}
    </Card>
  );
};

DocumentsWidget.propTypes = {
  id: PropTypes.number,
  type: PropTypes.string,
};

export default DocumentsWidget;
